<template>
	<div class="task-list-cell-due-date">
		<AppDatePickerInline
			:open
			:model-value="committedDueDateTime"
			:format="dateFormat"
			check-weekends
			append-to-body
			:date-disabled="isBeforeStartDate"
			@update:model-value="onUpdateDate"
			@close="open = false"
		>
			<button
				type="button"
				class="task-list-cell-due-date__trigger"
				@click="open = !open"
			>
				<TaskDueDate
					:is-done="isDone"
					:due-date-time="committedDueDateTime"
					:show-time="showTime"
				/>
			</button>
		</AppDatePickerInline>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { AppDatePickerInline } from '@/features/App';
import { TaskDueDate } from '../../TaskDueDate';
import {
	useTaskListTaskSaveMutation,
	type Task,
	type TaskListTaskFieldsFragment,
	type TaskListTaskFragment,
} from '@/generated/graphql';
import { useFormatter, useOptimisticSave } from '@/composables';
import { resetTime } from '@/utils/formatting';

export type TaskListCellDueDateProps = {
	taskId: Task['id'];
	dueDateTime?: Task['dueDateTime'];
	isDone: Task['isDone'];
	showTime?: boolean;
	startDate?: TaskListTaskFieldsFragment['startDate'];
};

const {
	taskId,
	dueDateTime = '',
	isDone,
	startDate = undefined,
	showTime,
} = defineProps<TaskListCellDueDateProps>();

const emit = defineEmits<{
	'task-updated': [task?: TaskListTaskFragment];
}>();

const { dateFormatForSUIdatePicker: dateFormat } = useFormatter();
const { mutate } = useTaskListTaskSaveMutation();

const { committedValue: committedDueDateTime, save } = useOptimisticSave<
	string | undefined
>(() => dueDateTime ?? undefined);
const open = ref(false);

const isBeforeStartDate = (date: string) => {
	if (!startDate) {
		return false;
	}

	return resetTime(date) < resetTime(startDate);
};

const onUpdateDate = async (value: string) => {
	open.value = false;
	if (!value) {
		return;
	}

	await save(value, async () => {
		const result = await mutate({
			input: {
				taskId,
				dueDateTime: value,
			},
		});

		if (result?.data?.TaskSave) {
			emit('task-updated', result.data.TaskSave);
			const saved = result.data.TaskSave;
			const nextDue = showTime ? saved.dueDateTime : saved.dueDate;
			return nextDue || undefined;
		}
	});
};
</script>

<style lang="scss">
.task-list-cell-due-date {
	width: 100%;

	&__trigger {
		display: inline-flex;
		justify-content: flex-end;
		align-items: center;
		width: calc(100% + 1rem);
		height: 4rem;
		border-radius: 0.5rem;
		text-align: right;
		margin: -1rem;
		padding: 1rem;

		&:hover {
			background-color: $neutral-20;
		}
	}

	.mx-datepicker {
		width: 100%;
	}
}
</style>
